import { useEffect, useMemo, useState } from "react";
import {
    Box,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControl,
    Grid,
    InputAdornment,
    InputLabel,
    MenuItem,
    Select,
    TextField,
    Typography,
} from "@mui/material";
import { getStones, getStoneVariations } from "../../../helpers/httpRequests/stones";
import { formatOptions, formatOptionsWithPrice, moneyFormat } from "../../../utils/functions";
import { useQuery } from "@tanstack/react-query";
import appToast from "../../../components/appToast";

const EMPTY = {
    stoneType: "",
    stoneVariation: "",
    quantity: 1,
    description: "",
};

export default function SelectStonesModal({ open, handleClose, handleAddStone }) {
    const [stoneType, setStoneType] = useState(EMPTY.stoneType);
    const [stoneVariation, setStoneVariation] = useState(EMPTY.stoneVariation);
    const [quantity, setQuantity] = useState(EMPTY.quantity);
    const [description, setDescription] = useState(EMPTY.description);

    const { data } = useQuery({ queryKey: ["getStones"], queryFn: getStones });
    const stoneTypes = formatOptions(data);

    const { data: stoneVariationsData, isLoading: isLoadingVariations } = useQuery({
        queryKey: ["getStoneVariations", stoneType],
        queryFn: () => getStoneVariations(stoneType),
        enabled: Boolean(stoneType),
    });

    const stoneVariations = formatOptionsWithPrice(stoneVariationsData);

    useEffect(() => {
        if (!open) {
            setStoneType(EMPTY.stoneType);
            setStoneVariation(EMPTY.stoneVariation);
            setQuantity(EMPTY.quantity);
            setDescription(EMPTY.description);
        }
    }, [open]);

    const unitPrice = useMemo(() => {
        const variation = stoneVariations.find((obj) => String(obj.value) === String(stoneVariation));
        return Number(variation?.price || 0);
    }, [stoneVariation, stoneVariations]);

    const totalPrice = unitPrice * Number(quantity || 0);

    const handleStoneTypeChange = (e) => {
        setStoneType(e.target.value);
        setStoneVariation("");
    };

    const handleChooseStone = () => {
        const qty = Number(quantity);
        if (!stoneType || !stoneVariation || !qty || qty < 1) {
            appToast.error("Escolha a pedra, a variação e a quantidade.");
            return;
        }

        const stoneName = stoneTypes.find((obj) => String(obj.value) === String(stoneType))?.label;
        const variationName = stoneVariations.find((obj) => String(obj.value) === String(stoneVariation))?.label;

        handleAddStone({
            stone_id: stoneType,
            stone_name: stoneName,
            variation_id: stoneVariation,
            variation_name: variationName,
            price: unitPrice,
            quantity: qty,
            observation: description.trim(),
        });
        handleClose();
    };

    return (
        <Dialog
            open={open}
            onClose={handleClose}
            fullWidth
            maxWidth="sm"
            aria-labelledby="stone-dialog-title"
            slotProps={{
                paper: {
                    sx: {
                        bgcolor: "background.paper",
                        border: "1px solid",
                        borderColor: "divider",
                        backgroundImage: "none",
                    },
                },
            }}
        >
            <DialogTitle id="stone-dialog-title" sx={{ fontFamily: "DM Serif Display" }}>
                Incluir pedra
            </DialogTitle>
            <DialogContent>
                <Grid container spacing={2} sx={{ mt: 0.5 }}>
                    <Grid size={{ xs: 12, sm: 6 }}>
                        <FormControl fullWidth>
                            <InputLabel id="stone-type-label">Tipo</InputLabel>
                            <Select
                                labelId="stone-type-label"
                                value={stoneType}
                                label="Tipo"
                                onChange={handleStoneTypeChange}
                            >
                                <MenuItem value="" disabled>Selecionar</MenuItem>
                                {stoneTypes.map((option) => (
                                    <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6 }}>
                        <FormControl fullWidth>
                            <InputLabel id="stone-variation-label">Variação</InputLabel>
                            <Select
                                labelId="stone-variation-label"
                                value={stoneVariation}
                                label="Variação"
                                onChange={(e) => setStoneVariation(e.target.value)}
                                disabled={!stoneType || isLoadingVariations}
                            >
                                <MenuItem value="" disabled>
                                    {isLoadingVariations ? "Carregando..." : "Selecionar"}
                                </MenuItem>
                                {stoneVariations.map((option) => (
                                    <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Grid>
                    <Grid size={{ xs: 12, sm: 4 }}>
                        <TextField
                            fullWidth
                            disabled={!stoneVariation}
                            label="Quantidade"
                            value={quantity}
                            onChange={(e) => setQuantity(e.target.value)}
                            type="number"
                            slotProps={{
                                input: {
                                    endAdornment: <InputAdornment position="end">un</InputAdornment>,
                                },
                                htmlInput: { min: 1, step: 1 },
                            }}
                        />
                    </Grid>
                    <Grid size={{ xs: 12, sm: 8 }}>
                        <TextField
                            fullWidth
                            label="Observação"
                            placeholder="Corte, posição ou detalhe. Opcional."
                            value={description}
                            onChange={(e) => setDescription(e.target.value)}
                        />
                    </Grid>
                </Grid>
            </DialogContent>
            <DialogActions sx={{ px: 3, pb: 2.5, justifyContent: "space-between" }}>
                <Box>
                    <Typography sx={{ fontSize: "0.75rem", color: "text.secondary" }}>
                        Valor das pedras
                    </Typography>
                    <Typography sx={{ fontFamily: "DM Serif Display", fontVariantNumeric: "tabular-nums" }}>
                        {moneyFormat(totalPrice)}
                    </Typography>
                </Box>
                <Box sx={{ display: "flex", gap: 1 }}>
                    <Button variant="outlined" color="primary" onClick={handleClose}>
                        Cancelar
                    </Button>
                    <Button variant="contained" color="secondary" onClick={handleChooseStone}>
                        Incluir
                    </Button>
                </Box>
            </DialogActions>
        </Dialog>
    );
}
